// Wireframe dlgNewModel — modal "Novo modelo", markup LITERAL (varredura v2
// §5.6-C3): WfModalShell + .wf-field/.nc-2col + chip-editor de níveis de
// reasoning (.chip-edit/.rchip — "Enter adiciona", × remove). Provedor ·
// identificador (model_key) · custos · descrição · reasoning · OAuth/API Key
// → POST /v1/llm-models.
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { api, type CanonicalEffort, type LlmProvider } from '@/lib/api';
import { WfModalShell } from '@/components/settings/WfModalShell';
import { useToast } from '@/components/ui/Toast';
import { EFFORT_LEVELS, mutationErrorMessage } from './modelHelpers';

interface ModelCreateDialogProps {
  open: boolean;
  providers: LlmProvider[];
  /** Provedor pré-selecionado (criação "a partir do provedor"). */
  initialProviderId?: string;
  onClose: () => void;
}

export function ModelCreateDialog({ open, providers, initialProviderId, onClose }: ModelCreateDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [providerId, setProviderId] = useState(initialProviderId ?? providers[0]?.id ?? '');
  const [modelKey, setModelKey] = useState('');
  const [inputCost, setInputCost] = useState('');
  const [outputCost, setOutputCost] = useState('');
  const [notes, setNotes] = useState('');
  const [reasoningEnabled, setReasoningEnabled] = useState(false);
  const [levels, setLevels] = useState<CanonicalEffort[]>(['low', 'medium', 'high']);
  const [levelInput, setLevelInput] = useState('');
  const [viaOauth, setViaOauth] = useState(false);
  const [viaApiKey, setViaApiKey] = useState(true);
  const [keyError, setKeyError] = useState<string | undefined>();

  // O caller remonta este componente a cada abertura (via `key`), então o estado
  // inicial (inclusive o provedor pré-selecionado) é sempre fresco.
  const handleClose = () => {
    onClose();
  };

  const mutation = useMutation({
    mutationFn: (payload: Parameters<typeof api.createLlmModel>[0]) => api.createLlmModel(payload),
    onSuccess: (model) => {
      queryClient.invalidateQueries({ queryKey: ['llm-catalog'] });
      toast({ message: `Modelo ${model.model_key} criado.`, type: 'success' });
      handleClose();
    },
    onError: (err) => {
      toast({ message: mutationErrorMessage(err, 'criar o modelo'), type: 'error' });
    },
  });

  // Chip-editor (wireframe rsKey): Enter adiciona o nível digitado, validando
  // contra os níveis canônicos (CanonicalEffort) — não inventamos níveis.
  const addLevel = () => {
    const raw = levelInput.trim().toLowerCase();
    if (!raw) return;
    if (!(EFFORT_LEVELS as string[]).includes(raw)) {
      toast({
        message: `Nível inválido: "${raw}". Níveis canônicos: ${EFFORT_LEVELS.join(', ')}.`,
        type: 'warning',
      });
      return;
    }
    const lvl = raw as CanonicalEffort;
    setLevels((cur) => (cur.includes(lvl) ? cur : [...cur, lvl]));
    setLevelInput('');
  };

  const removeLevel = (lvl: CanonicalEffort) => {
    setLevels((cur) => cur.filter((l) => l !== lvl));
  };

  const handleCreate = () => {
    const key = modelKey.trim();
    if (!key) {
      setKeyError('Informe o identificador do modelo (ex: claude-sonnet-4-5).');
      return;
    }
    if (!providerId) {
      toast({ message: 'Selecione um provedor.', type: 'warning' });
      return;
    }
    const parsedIn = Number(inputCost || '0');
    const parsedOut = Number(outputCost || '0');
    if (Number.isNaN(parsedIn) || Number.isNaN(parsedOut)) {
      toast({ message: 'Custos devem ser numéricos (USD / 1M tokens).', type: 'warning' });
      return;
    }
    if (reasoningEnabled && levels.length === 0) {
      toast({ message: 'Reasoning habilitado exige ao menos um nível suportado.', type: 'warning' });
      return;
    }
    setKeyError(undefined);

    const supported = reasoningEnabled
      ? [...levels].sort((a, b) => EFFORT_LEVELS.indexOf(a) - EFFORT_LEVELS.indexOf(b))
      : [];

    mutation.mutate({
      provider_id: providerId,
      model_key: key,
      display_name: key,
      input_cost_per_m: parsedIn,
      output_cost_per_m: parsedOut,
      notes: notes.trim() || null,
      supported_effort_levels: supported,
      default_effort_level: supported.includes('medium') ? 'medium' : supported[0] ?? null,
      available_via_oauth: viaOauth,
      available_via_api_key: viaApiKey,
    });
  };

  return (
    <WfModalShell
      open={open}
      onClose={handleClose}
      icon={<Plus className="ic" style={{ color: 'var(--wf-accent-text)' }} aria-hidden />}
      title="Novo modelo"
      footer={
        <>
          <button type="button" className="wf-btn wf-btn-ghost" onClick={handleClose} disabled={mutation.isPending}>
            Cancelar
          </button>
          <button type="button" className="wf-btn wf-btn-primary" onClick={handleCreate} disabled={mutation.isPending}>
            {mutation.isPending ? 'Criando…' : 'Criar modelo'}
          </button>
        </>
      }
    >
      <div className="wf-field">
        <label className="wf-label">Provedor</label>
        <select
          className="wf-select"
          value={providerId}
          onChange={(e) => setProviderId(e.target.value)}
        >
          {providers.map((p) => (
            <option key={p.id} value={p.id}>{p.display_name}</option>
          ))}
        </select>
      </div>

      <div className="wf-field">
        <label className="wf-label">Identificador do modelo</label>
        <input
          className="wf-input"
          value={modelKey}
          onChange={(e) => {
            setModelKey(e.target.value);
            if (keyError) setKeyError(undefined);
          }}
          placeholder="ex: claude-sonnet-4-5"
          aria-invalid={Boolean(keyError)}
        />
        {keyError && (
          <p style={{ margin: '5px 0 0', fontSize: '.72rem', color: 'var(--wf-danger-text)' }}>{keyError}</p>
        )}
      </div>

      <div className="nc-2col">
        <div className="wf-field">
          <label className="wf-label">Custo token entrada (USD / 1M)</label>
          <input
            className="wf-input"
            inputMode="decimal"
            value={inputCost}
            onChange={(e) => setInputCost(e.target.value)}
            placeholder="0.00"
          />
        </div>
        <div className="wf-field">
          <label className="wf-label">Custo token saída (USD / 1M)</label>
          <input
            className="wf-input"
            inputMode="decimal"
            value={outputCost}
            onChange={(e) => setOutputCost(e.target.value)}
            placeholder="0.00"
          />
        </div>
      </div>

      <div className="wf-field">
        <label className="wf-label">Descrição curta</label>
        <textarea
          className="wf-textarea"
          style={{ minHeight: 64 }}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Para que serve este modelo"
        />
      </div>

      <div className="nc-2col">
        <div className="wf-field">
          <label className="wf-label">Reasoning</label>
          <select
            className="wf-select"
            value={reasoningEnabled ? 'on' : 'off'}
            onChange={(e) => setReasoningEnabled(e.target.value === 'on')}
          >
            <option value="off">Desabilitado</option>
            <option value="on">Habilitado</option>
          </select>
        </div>
        <div className="wf-field">
          <label className="wf-label">Níveis suportados (Enter adiciona)</label>
          <div className="chip-edit">
            {levels.map((lvl) => (
              <span key={lvl} className="rchip">
                {lvl}
                <button
                  type="button"
                  aria-label={`Remover nível ${lvl}`}
                  onClick={() => removeLevel(lvl)}
                  disabled={!reasoningEnabled}
                >
                  ×
                </button>
              </span>
            ))}
            <input
              value={levelInput}
              onChange={(e) => setLevelInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addLevel();
                }
              }}
              placeholder="ex: max e Enter"
              aria-label="Adicionar nível de reasoning"
              disabled={!reasoningEnabled}
            />
          </div>
        </div>
      </div>

      <div className="nc-2col">
        <div className="wf-field">
          <label className="wf-label">Ativo via OAuth</label>
          <select
            className="wf-select"
            value={viaOauth ? 'yes' : 'no'}
            onChange={(e) => setViaOauth(e.target.value === 'yes')}
          >
            <option value="no">Não</option>
            <option value="yes">Sim</option>
          </select>
        </div>
        <div className="wf-field">
          <label className="wf-label">Ativo via API Key</label>
          <select
            className="wf-select"
            value={viaApiKey ? 'yes' : 'no'}
            onChange={(e) => setViaApiKey(e.target.value === 'yes')}
          >
            <option value="yes">Sim</option>
            <option value="no">Não</option>
          </select>
        </div>
      </div>
    </WfModalShell>
  );
}
